import React, { useState, useEffect } from 'react'
import { BRANDS, PRODUCT_TYPES, GENDERS } from '../lib/constants'
import {
  getDuty, getLandedUSD, getLandedGBP,
  getOffPriceWS, getOffPriceRRP, getOffPriceMargin,
  getStdWS, getStdRRP, getStdMargin,
  getTRSupply, getTRRRP, getTRMargin,
  getD2CRRP, getD2CMarginNoPP, formatPct, DEFAULT_SETTINGS
} from '../lib/pricing'

export default function ProductModal({ product, onSave, onClose, saving, settings = DEFAULT_SETTINGS }) {
  const [data, setData] = useState(product)

  useEffect(() => { setData(product) }, [product])

  const set = (key, val) => setData(prev => ({ ...prev, [key]: val }))

  // Live pricing preview
  const duty = getDuty(data)
  const lusd = getLandedUSD(data)
  const lgbp = getLandedGBP(data, settings)

  const pricing = [
    { label: 'Off-Price Retail', ws: `£${getOffPriceWS(data, 'GBP', settings).toFixed(2)}`, rrp: `£${getOffPriceRRP(data, 'GBP', settings)}`, margin: formatPct(getOffPriceMargin(data, 'GBP', settings)) },
    { label: 'Standard Wholesale', ws: `£${getStdWS(data, 'GBP', settings).toFixed(2)}`, rrp: `£${getStdRRP(data, 'GBP', settings)}`, margin: formatPct(getStdMargin(data, 'GBP', settings)) },
    { label: 'Travel Retail', ws: `£${getTRSupply(data, 'GBP', settings).toFixed(2)}`, rrp: `£${getTRRRP(data, 'GBP', settings)}`, margin: formatPct(getTRMargin(data, 'GBP', settings)) },
    { label: 'D2C', ws: '—', rrp: `£${getD2CRRP(data, 'GBP', settings)}`, margin: formatPct(getD2CMarginNoPP(data, 'GBP', settings)) },
  ]

  function handleSubmit(e) {
    e.preventDefault()
    if (!data.sku || !data.brand) { alert('SKU and Brand are required'); return }
    onSave(data)
  }

  const F = ({ label, id, type = 'text', value, onChange, readOnly, placeholder }) => (
    <div className="form-field">
      <label className="form-label">{label}</label>
      <input className={`form-input ${readOnly ? 'readonly' : ''}`} type={type} value={value || ''} onChange={e => onChange(e.target.value)} readOnly={readOnly} placeholder={placeholder} />
    </div>
  )

  const S = ({ label, id, value, onChange, children }) => (
    <div className="form-field">
      <label className="form-label">{label}</label>
      <select className="form-input" value={value || ''} onChange={e => onChange(e.target.value)}>{children}</select>
    </div>
  )

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{data.id ? 'Edit product' : 'Add product'}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>

            <div className="form-section">Identity</div>
            <div className="form-grid-2">
              <F label="YVG SKU *" value={data.sku} onChange={v => set('sku', v)} placeholder="e.g. WJW142" />
              <S label="Brand *" value={data.brand} onChange={v => set('brand', v)}>
                <option value="">Select brand</option>
                {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
              </S>
              <F label="Supplier code" value={data.supplier_code} onChange={v => set('supplier_code', v)} />
              <F label="EAN code" value={data.ean_code} onChange={v => set('ean_code', v)} placeholder="13-digit barcode" />
              <F label="HTS code" value={data.hts_code} onChange={v => set('hts_code', v)} />
              <F label="TREX" value={data.trex} onChange={v => set('trex', v)} />
              <F label="Factory SKU" value={data.factory_sku} onChange={v => set('factory_sku', v)} />
              <F label="Factory MOQ" type="number" value={data.factory_moq} onChange={v => set('factory_moq', v)} />
              <S label="Product type" value={data.product_type} onChange={v => set('product_type', v)}>
                {PRODUCT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </S>
              <S label="Gender" value={data.gender} onChange={v => set('gender', v)}>
                {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
              </S>
              <F label="Country of origin" value={data.country_of_origin} onChange={v => set('country_of_origin', v)} />
              <F label="Production time (wks EXW)" type="number" value={data.production_time_weeks} onChange={v => set('production_time_weeks', v)} />
            </div>

            <div className="form-section">Specification</div>
            <div className="form-grid-2">
              <F label="Case size MM" value={data.case_size_mm} onChange={v => set('case_size_mm', v)} placeholder="e.g. 42MM" />
              <F label="Movement model" value={data.movement_model} onChange={v => set('movement_model', v)} placeholder="e.g. Japanese" />
              <F label="Battery" value={data.battery} onChange={v => set('battery', v)} />
              <F label="Size MM" value={data.size_mm} onChange={v => set('size_mm', v)} />
              <F label="Base metal" value={data.base_metal} onChange={v => set('base_metal', v)} />
              <F label="Plating type" value={data.plating_type} onChange={v => set('plating_type', v)} />
              <F label="Stone / gemstone" value={data.stone_gemstone} onChange={v => set('stone_gemstone', v)} />
              <F label="Product weight (kg)" type="number" value={data.product_weight} onChange={v => set('product_weight', v)} />
              <F label="Weight incl. packaging (kg)" type="number" value={data.weight_including_packaging} onChange={v => set('weight_including_packaging', v)} />
            </div>
            <div className="form-field" style={{ marginTop: 8 }}>
              <label className="form-label">Full specification</label>
              <textarea className="form-input" rows={3} value={data.specification || ''} onChange={e => set('specification', e.target.value)} style={{ height: 'auto' }} />
            </div>

            <div className="form-section">Costing (USD)</div>
            <div className="form-grid-3">
              <F label="Factory cost $" type="number" value={data.factory_cost} onChange={v => set('factory_cost', v)} />
              <F label="Packaging $" type="number" value={data.packaging_cost} onChange={v => set('packaging_cost', v)} />
              <F label="Duty $ (auto 10%)" value={`$${duty.toFixed(2)}`} readOnly />
              <F label="Freight $" type="number" value={data.freight} onChange={v => set('freight', v)} />
              <F label="Landed USD" value={`$${lusd.toFixed(2)}`} readOnly />
              <F label="Landed GBP" value={`£${lgbp.toFixed(2)}`} readOnly />
            </div>

            {/* Live pricing preview */}
            {lusd > 0 && (
              <div className="pricing-preview">
                <div className="pricing-preview-title">Live pricing preview (GBP)</div>
                <div className="pricing-preview-grid">
                  {pricing.map(({ label, ws, rrp, margin }) => (
                    <div key={label} className="pricing-preview-card">
                      <div className="pp-label">{label}</div>
                      <div className="pp-row"><span>Supply/WS</span><span>{ws}</span></div>
                      <div className="pp-row"><span>RRP</span><strong>{rrp}</strong></div>
                      <div className="pp-row"><span>Margin</span><span>{margin}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="form-section">Media</div>
            <div className="form-grid-2">
              <F label="Packshot image URL" value={data.packshot_image} onChange={v => set('packshot_image', v)} placeholder="https://..." />
              <F label="Model image URL" value={data.model_image} onChange={v => set('model_image', v)} placeholder="https://..." />
              <F label="Reference image URL" value={data.reference_image} onChange={v => set('reference_image', v)} placeholder="https://..." />
              <F label="MSDS sheet URL" value={data.msds_sheet} onChange={v => set('msds_sheet', v)} placeholder="https://..." />
              <F label="User manual URL" value={data.user_manual} onChange={v => set('user_manual', v)} placeholder="https://..." />
            </div>

            <div className="form-section">Descriptions</div>
            <div className="form-field">
              <label className="form-label">English</label>
              <textarea className="form-input" rows={2} value={data.description_en || ''} onChange={e => set('description_en', e.target.value)} style={{ height: 'auto' }} />
            </div>
            <div className="form-grid-3">
              <div className="form-field">
                <label className="form-label">German</label>
                <input className="form-input" value={data.description_de || ''} onChange={e => set('description_de', e.target.value)} />
              </div>
              <div className="form-field">
                <label className="form-label">Spanish</label>
                <input className="form-input" value={data.description_es || ''} onChange={e => set('description_es', e.target.value)} />
              </div>
              <div className="form-field">
                <label className="form-label">French</label>
                <input className="form-input" value={data.description_fr || ''} onChange={e => set('description_fr', e.target.value)} />
              </div>
            </div>

            <div className="form-section">Packaging</div>
            <div className="form-grid-2">
              <F label="Packaging SKU" value={data.packaging_sku} onChange={v => set('packaging_sku', v)} />
              <F label="Packaging dimensions" value={data.packaging_dimensions} onChange={v => set('packaging_dimensions', v)} />
              <F label="Qty per case" type="number" value={data.qty_per_case} onChange={v => set('qty_per_case', v)} />
              <F label="Case size CM" value={data.case_size_cm} onChange={v => set('case_size_cm', v)} />
              <S label="Eco-friendly packaging" value={data.packaging_eco_friendly ? 'Y' : 'N'} onChange={v => set('packaging_eco_friendly', v === 'Y')}>
                <option value="N">No</option>
                <option value="Y">Yes</option>
              </S>
              <F label="Packaging instructions" value={data.packaging_instructions} onChange={v => set('packaging_instructions', v)} />
            </div>

            <div className="form-section">Stock</div>
            <div className="form-grid-3">
              <F label="UK stock (Flostream)" type="number" value={data.stock_uk} onChange={v => set('stock_uk', v)} />
              <F label="DE stock (Flostream)" type="number" value={data.stock_de} onChange={v => set('stock_de', v)} />
              <F label="Avg weekly sales" type="number" value={data.avg_weekly_sales} onChange={v => set('avg_weekly_sales', v)} />
            </div>

            <div className="modal-footer">
              <button type="button" className="btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn primary" disabled={saving}>
                {saving ? 'Saving…' : data.id ? 'Update product' : 'Save product'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
